import {
  NarrativeCardIsomorphic,
  NARRATIVE_CARD_ISOMORPHIC_FRAGMENT,
} from '@/components/NarrativeCard';

import { gql, readFragment } from '@/utilities/gql';
import type { FragmentOf } from '@/utilities/gql';
import { useCallback } from 'react';
import type { AriaRole } from 'react';
import { useTracking } from 'react-tracking';
import { usePathname } from '@/hooks/location/usePathname';
import { NarrativeInvalidationBoundary } from '@/pages/Narrative/components/NarrativeInvalidationBoundary';
import { narrativeFvModalReturnPath } from '../../utils';

export const NARRATIVE_CARD_OPTION_FRAGMENT = gql(
  `
  fragment NarrativeCardOptionValuationOptionFields on ValuationOption @_unmask {
    type
    narrative {
      id
      ...NarrativeCardIsomorphicFields
    }
  }
`,
  [NARRATIVE_CARD_ISOMORPHIC_FRAGMENT]
);

type Props = {
  valuationOption: FragmentOf<typeof NARRATIVE_CARD_OPTION_FRAGMENT>;
  role?: AriaRole;
  onError: () => void;
};

const isSyntheticNarrative = (type: string): type is 'DCF' | 'ANALYSTS' =>
  ['DCF', 'ANALYSTS'].includes(type);

export const NarrativeCardOption = ({
  valuationOption,
  role,
  onError,
}: Props) => {
  const { trackEvent } = useTracking();
  const pathname = usePathname();

  const valuationOptionUnmasked = readFragment(
    NARRATIVE_CARD_OPTION_FRAGMENT,
    valuationOption
  );
  const { narrative, type } = valuationOptionUnmasked;
  const { company } = narrative ?? {};
  const { canonicalURL } = company ?? {};

  const handleCardClick = useCallback(() => {
    trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: narrative?.id,
      companyId: company?.id,
      valuationType: type,
    });
  }, [trackEvent, narrative?.id, company?.id, type]);

  if (!narrative) {
    console.error('Narrative is missing from valuation option');
    return null;
  }

  return (
    <div role={role}>
      <NarrativeInvalidationBoundary
        onSetPrimary={({ invalidatePriority }) =>
          invalidatePriority(['narrative-valuation-options'])
        }
      >
        <NarrativeCardIsomorphic
          className="rounded-none min-[375px]:rounded-x1_5"
          narrative={narrative}
          linkTo={
            isSyntheticNarrative(type) ? `${canonicalURL}/valuation` : undefined
          }
          returnPath={
            isSyntheticNarrative(type)
              ? undefined
              : narrativeFvModalReturnPath(pathname, company?.id)
          }
          onClick={handleCardClick}
          onSetFairValueError={onError}
        />
      </NarrativeInvalidationBoundary>
    </div>
  );
};
